import {
  SupplierProductOptionTuple,
  SupplierProductTuple,
  SupplierTuple,
} from "models/supplier"
//
import { queryCache, useQuery } from "react-query"

import { CellProps } from "components/TreeState"
import { ChangedPlanRecord } from "../types"
import { ListCell } from "./ListCell"
import { MediaType } from "routes/Base/Admin/GlobalMediaTypes/types"
import { Paginated } from "api/types"
import { fetchSupplierMediaProducts } from "api/suppliers"
import { get } from "lodash"
import { set } from "lodash/fp"
import { useHistory, useParams } from "react-router-dom"
import { useSnapshot } from "valtio"
import { useTableSource } from "../TableStateProvider"
import { useUndoStack } from "../useUndoStack"
import { useSendFeedBack } from "components/FeedbackSnackbar"
import { useI18n } from "providers/I18n"
import { ApiError } from "api/http/utils"

const TUPLE_ID_SEPARATOR = "/"

export function VendorProduct(props: CellProps) {
  const sendFeedback = useSendFeedBack()
  const { t } = useI18n()
  const history = useHistory()
  const { id: planId } = useParams<{ id: string }>()
  const { rowId: id, primaryKey, disabled } = props
  const tableSource = useTableSource()
  const readFrom = useSnapshot(tableSource.state)
  const globalUndoState = useUndoStack<ChangedPlanRecord>()
  const writeTo = tableSource.state

  const {
    type,
    mediaSemanticCode,
    ancestorMediaSemanticCode,
    packaged,
    packageId,
    mediaId,
    ancestorMediaId,
    status,
  } = readFrom[id] || {}

  const ps = new URLSearchParams([
    ["configScope", "MEDIA_PLAN"],
    ["configOwnerId", planId],
  ])

  const mediaTypes = queryCache.getQueryData<Paginated<MediaType>>([
    "media-types",
    ps.toString(),
  ])
  const mediaTypeId = type === "media_type_plan" ? mediaId : ancestorMediaId

  const defaultFieldIds = mediaTypes?.data?.find(
    (mt) => mt.id === mediaTypeId,
  )?.defaultFieldIdBySemantic

  const { vendor: vendorFieldId, vendorProduct: vendorProductFieldId } =
    defaultFieldIds || {}

  const fields = get(readFrom[id], primaryKey)

  const vendor =
    !!packaged && type === "line_item"
      ? (get(
          readFrom[packageId || ""],
          `${primaryKey}.${vendorFieldId}`,
        ) as SupplierTuple)
      : (fields?.[vendorFieldId] as SupplierTuple)

  const appliedMediaSemanticCode =
    !!packaged && type === "line_item"
      ? get(readFrom[packageId || ""], "ancestorMediaSemanticCode")
      : ancestorMediaSemanticCode

  const vendorProductTuple = get(
    readFrom[id],
    `${primaryKey}.${vendorProductFieldId}`,
  ) as SupplierProductTuple

  const selected = !!vendorProductTuple
    ? vendorProductTupleToString(vendorProductTuple)
    : undefined

  const mediaTypeSemanticCode =
    type === "media_type_plan" ? mediaSemanticCode : appliedMediaSemanticCode

  const vpp = new URLSearchParams()
  !!mediaTypeSemanticCode &&
    vpp.set("mediaTypeSemanticCode", mediaTypeSemanticCode)
  vendor?.mediaOwnerId && vpp.set("mediaOwnerId", vendor.mediaOwnerId)
  vendor?.intermediaryId && vpp.set("intermediaryId", vendor.intermediaryId)

  const cached = queryCache.getQueryData<SupplierProductOptionTuple[]>([
    "vendor-products",
    planId,
    vpp.toString(),
  ])
  const { data: vendorProducts } = useQuery(
    ["vendor-products", planId, vpp.toString()],
    fetchSupplierMediaProducts,
    {
      enabled: !!mediaTypeSemanticCode && !cached,
      initialData: cached,
      refetchOnMount: !cached,
      onError: (error: ApiError) => {
        sendFeedback(t(error.message), "error")
        if (error.info.status === 404) {
          setTimeout(() => history.push("/plans"), 2000)
        }
      },
    },
  )

  const onChange = (vendorProductString?: string) => {
    if (vendorProductString) {
      const { mediaOwnerId, intermediaryId } = normalize(vendorProductString)
      const vendor = vendorProducts?.find(
        (vp) =>
          vp.mediaOwner.id === mediaOwnerId &&
          vp.intermediary?.id === intermediaryId,
      )

      // on vendor product change clear the rate card
      const { rateCardCost } = defaultFieldIds || {}
      writeTo[id] = set(`${primaryKey}.${rateCardCost}`, "", writeTo[id])

      if (!!vendor) {
        const newValue = {
          mediaOwnerId: vendor.mediaOwner.id,
          intermediaryId: vendor.intermediary?.id,
        }
        globalUndoState.push([writeTo[id] as ChangedPlanRecord])
        writeTo[id] = set(
          `${primaryKey}.${vendorFieldId}`,
          newValue,
          writeTo[id],
        )
        if (!!packaged && type === "line_item" && !!packageId) {
          writeTo[packageId] = set(
            `${primaryKey}.${vendorFieldId}`,
            newValue,
            writeTo[packageId],
          )
        }
      }
    }
  }

  const options = vendorProducts?.map(getVendorProductTupleOption) || []

  const isEditable = !status || status === "DRAFT"

  const isDisabled =
    primaryKey === "buyingMediaFields" ||
    !isEditable ||
    type === "package" ||
    disabled

  return (
    <ListCell
      {...props}
      bulkDisabled={true}
      options={options}
      onChange={onChange}
      selected={selected}
      normalize={normalize}
      disabled={isDisabled}
      dataKey={`${primaryKey}.${vendorProductFieldId}`}
    />
  )
}

export function vendorProductTupleToString(vt: SupplierProductTuple) {
  const value = vt.intermediaryId
    ? [vt.mediaOwnerId, vt.intermediaryId, vt.supplierProductId].join(
        TUPLE_ID_SEPARATOR,
      )
    : [vt.mediaOwnerId, vt.supplierProductId].join(TUPLE_ID_SEPARATOR)

  return value
}

function getOptionValue(vt: SupplierProductOptionTuple) {
  const mediaOwnerId = vt.mediaOwner.id
  const intermediaryId = vt.intermediary?.id
  const supplierProductId = vt.id
  const value = intermediaryId
    ? [mediaOwnerId, intermediaryId, supplierProductId].join(TUPLE_ID_SEPARATOR)
    : [mediaOwnerId, supplierProductId].join(TUPLE_ID_SEPARATOR)

  return value
}

function getOptionLabel(vt: SupplierProductOptionTuple) {
  return `${vt.name} (${vt.mediaOwner.name}${
    vt?.intermediary ? `/ ${vt.intermediary.name}` : ""
  })`
}

export function getVendorProductTupleOption(vt: SupplierProductOptionTuple) {
  return { label: getOptionLabel(vt), value: getOptionValue(vt) }
}

function normalize(stringId: string): SupplierProductTuple {
  const [mediaOwnerId, second, third] = stringId.split(TUPLE_ID_SEPARATOR)
  return {
    mediaOwnerId,
    intermediaryId: third && second,
    supplierProductId: third || second,
  }
}
